import type React from 'react';

import { Link, SocialSharing, SocialSharingGroup, Text } from '@change/ds-core-ui';
import { FacebookRegularIcon, TwitterIcon } from '@change/ds-icons';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { Join } from '#src/app/shared/components/layout/misc';

import { CommentDelete } from './components/CommentDelete';
import { CommentLikes } from './components/CommentLikes';
import { CommentReportAbuse } from './components/CommentReportAbuse';
import { useCommentFooter } from './hooks/useCommentFooter';
import { useCommentShares } from './hooks/useCommentShares';

export const CommentFooter: React.FC = () => {
	const {
		data: { author, authorUrl, dateRelative, location, canReport, canDelete, comment, petition, trackingProperties },
		actions: { handleDeletedComment },
	} = useCommentFooter();

	const {
		data: { shareActions, labels, a11Labels },
	} = useCommentShares(comment, petition, { viewContext: 'petitions_comments_show' });

	return (
		<div>
			<div className="mb-4 flex gap-4">
				<Text variant="body" size="x-small" className="flex-1" data-qa="comment-author">
					{authorUrl ? (
						<Link size="x-small" href={authorUrl} bold>
							{author}
						</Link>
					) : (
						<span className="font-bold">{author}</span>
					)}
					, {location}
				</Text>
				<Text variant="body" size="x-small" className="whitespace-nowrap" data-qa="comment-date">
					{dateRelative}
				</Text>
			</div>
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex h-8 flex-wrap items-center gap-4">
					<TrackingContext properties={trackingProperties}>
						<CommentLikes comment={comment} />
					</TrackingContext>
					<SocialSharingGroup layout="inline" className="items-center gap-4">
						{shareActions.map(({ channel, ShareAction }) => (
							<div key={channel} className="flex items-center">
								<ShareAction>
									<SocialSharing
										icon={channel === 'facebook' ? <FacebookRegularIcon /> : <TwitterIcon />}
										variant={channel}
										label=""
										aria-label={a11Labels[channel]}
										size="x-small"
										className="m-0 mr-2 w-fit"
										data-qa={`comment-share-${channel}`}
									/>
								</ShareAction>
								<Text variant="body" size="small">
									{labels[channel]}
								</Text>
							</div>
						))}
					</SocialSharingGroup>
				</div>
				<div className="flex items-center">
					<Join
						separator={
							<Text variant="body" size="x-small" className="text-typography-weak mx-2">
								·
							</Text>
						}
					>
						{canDelete && (
							<TrackingContext properties={trackingProperties}>
								<CommentDelete size="small" comment={comment} onDeleted={handleDeletedComment} />
							</TrackingContext>
						)}
						{canReport && <CommentReportAbuse size="x-small" comment={comment} petition={petition} />}
					</Join>
				</div>
			</div>
		</div>
	);
};
